import { injectable, inject } from 'inversify';
import type { IAuthRepository, LoginRequest } from '@/domain/repositories/IAuthRepository';
import { LoginCommand } from '@/application/commands/auth/LoginCommand';
import { User } from '@/domain/entities/User';
import { InvalidCredentialsError } from '@/domain/errors/InvalidCredentialsError';
import { TYPES } from '@/di/tokens';

export interface LoginActionResult {
  token: string | null;
  user: User | null;
  requiresTwoFactor: boolean;
  requiresTwoFactorSetup?: boolean;
  twoFactorSecret?: string;
  twoFactorQrCodeUrl?: string;
  expiresIn?: number;
}

@injectable()
export class LoginAction {
  constructor(
    @inject(TYPES.IAuthRepository) private authRepository: IAuthRepository
  ) {}

  async execute(command: LoginCommand): Promise<LoginActionResult> {
    try {
      const request: LoginRequest = {
        username: command.username,
        password: command.password,
        twoFactorCode: command.twoFactorCode,
        twoFactorSecret: command.twoFactorSecret,
      };

      const response = await this.authRepository.login(request);

      // Si hay token y usuario, guardar en localStorage
      if (response.token && response.user) {
        try {
          const tenant = response.user.getTenant();
          
          // Guardar token en múltiples lugares para compatibilidad
          localStorage.setItem('admin_token', response.token);
          localStorage.setItem('authToken', response.token);
          localStorage.setItem('token', response.token);
          
          const tenantData = tenant
            ? {
                id: tenant.id.getValue(),
                nombre: tenant.nombre,
                emailAdministrador: tenant.emailAdministrador.getValue(),
              }
            : null;
          
          const userData = {
            id: response.user.id.getValue(),
            username: response.user.username,
            email: response.user.email.getValue(),
            role: response.user.role,
            tenant: tenantData,
            createdAt: response.user.createdAt.toISOString(),
            lastLogin: response.user.lastLogin?.toISOString(),
            mustChangePassword: response.user.mustChangePassword,
          };
          
          localStorage.setItem('admin_user', JSON.stringify(userData));
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
          // Continuar aunque falle el guardado en localStorage
        }
      }

      return {
        token: response.token,
        user: response.user,
        requiresTwoFactor: response.requiresTwoFactor,
        requiresTwoFactorSetup: response.requiresTwoFactorSetup,
        twoFactorSecret: response.twoFactorSecret,
        twoFactorQrCodeUrl: response.twoFactorQrCodeUrl,
        expiresIn: response.expiresIn,
      };
    } catch (error) {
      console.error('LoginAction error:', error);
      if (error instanceof InvalidCredentialsError) {
        throw error;
      }
      // Si es un error desconocido, lanzar con el mensaje original si existe
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      throw new InvalidCredentialsError(errorMessage);
    }
  }
}
